import { NOTE_BOX_KEYS, type NoteBoxKey } from './note-box';

export interface RouteConfig {
  path: string;
  wrapClass: string;
  title?: string; // 페이지 제목 (선택사항)
}

export interface MenuItemConfig {
  relativePath: string;
  fullPath: string;
  wrapClass: string;
  title: string; // 메뉴 제목
  description?: string; // 페이지 설명 (선택)
  noteBoxKey?: NoteBoxKey;
  iconOff: string; // 비활성 아이콘 경로
  iconOn: string; // 활성 아이콘 경로
  activePaths?: string[]; // 활성화될 경로들 (정확한 경로 매칭)
  startsWithPaths?: readonly string[]; // 활성화될 경로들 (경로로 시작하는 경우)
  section?: 'mypage' | 'seller' | 'admin'; // 섹션 구분
  requiresSeller?: boolean; // 전문가일 때만 표시 (기본값: false, 항상 표시)
}

/**
 * 모든 라우트 설정 (경로 + wrap 클래스)
 * URL 변경 시 여기서만 수정하면 됨
 */
export const ROUTE_CONFIGS = {
  // 공통
  HOME: { path: '/', wrapClass: 'main_wrap', title: '홈' },
  SIGN_IN: { path: '/sign-in', wrapClass: 'login_wrap', title: '로그인' },
  SIGN_UP: { path: '/sign-up', wrapClass: 'join_wrap', title: '회원가입' },
  FIND_ID: { path: '/find-id', wrapClass: 'login_wrap', title: '아이디 찾기' },
  RESET_PASSWORD: { path: '/reset-password', wrapClass: 'login_wrap', title: '비밀번호 재설정' },
  PRODUCT: { path: '/products', wrapClass: 'product_wrap', title: '분석 스토어' },
  PRODUCT_INTRO: { path: '/product-intro', wrapClass: 'product_wrap', title: '제품 소개' },
  MARKETPLACE: { path: '/products', wrapClass: 'marketplace_wrap', title: '분석 스토어' },
  PRODUCT_DETAIL: { path: '/products/:id', wrapClass: 'marketplaceDetail_wrap', title: '상품 상세' },
  EDUCATION: { path: '/education', wrapClass: 'curriculum_wrap', title: '교육' },
  EDUCATION_COURSE: { path: '/education-course', wrapClass: 'curriculum_wrap', title: '교육 과정' },
  ANALYSIS_REQUEST: { path: '/analysis-request', wrapClass: 'analyze_wrap', title: '분석 의뢰' },
  INQUIRY: { path: '/inquiry', wrapClass: 'contact_wrap', title: '문의하기' },
  EYET_DOWNLOAD: { path: '/eyet-downloadEyet', wrapClass: 'download_wrap', title: 'EyeT 다운로드' },
  SERVICE: { path: '/service', wrapClass: 'download_wrap', title: '고객센터' },
  TERMS: { path: '/terms', wrapClass: 'mypage_wrap mypageRecharge_wrap', title: '약관 및 개인정보' },
  EXPERT_CENTER: { path: '/expert-center', wrapClass: 'mypage_wrap mypageRecharge_wrap', title: '전문가 센터' },

  ADMIN: {
    basePath: '/admin',
    routes: {
      SELLER_APPROVAL: {
        relativePath: 'seller-approval',
        fullPath: '/admin/seller-approval',
        wrapClass: 'mypage_wrap',
        title: '전문가 승인',
        iconOff: '/publish/img/webp/menu_icon8_off.webp', // 변경
        iconOn: '/publish/img/webp/menu_icon8_on.webp', // 변경
        section: 'admin',
      },
      PRODUCT_APPROVAL: {
        relativePath: 'product-approval',
        fullPath: '/admin/product-approval',
        wrapClass: 'mypage_wrap',
        title: '상품 승인',
        iconOff: '/publish/img/webp/menu_icon9_off.webp', // 변경
        iconOn: '/publish/img/webp/menu_icon9_on.webp', // 변경
        section: 'admin',
      },
      ANALYSIS_REQUEST: {
        relativePath: 'analysis-request',
        fullPath: '/admin/analysis-request',
        wrapClass: 'mypage_wrap',
        title: '분석 의뢰 관리',
        iconOff: '/publish/img/webp/menu_icon3_off.webp', // 변경
        iconOn: '/publish/img/webp/menu_icon3_on.webp', // 변경
        section: 'admin',
      },
      INQUIRY: {
        relativePath: 'inquiry',
        fullPath: '/admin/inquiry',
        wrapClass: 'mypage_wrap',
        title: '문의 관리',
        iconOff: '/publish/img/webp/menu_icon4_off.webp', // 변경
        iconOn: '/publish/img/webp/menu_icon4_on.webp', // 변경
        section: 'admin',
      },
      EDUCATION_MANAGEMENT: {
        relativePath: 'education-management',
        fullPath: '/admin/education-management',
        wrapClass: 'mypage_wrap',
        title: '교육 관리',
        iconOff: '/publish/img/webp/menu_icon6_off.webp', // 변경
        iconOn: '/publish/img/webp/menu_icon6_on.webp', // 변경
        startsWithPaths: ['/admin/education-management'],
        section: 'admin',
      },
      CLIENT_MANAGEMENT: {
        relativePath: 'client-management',
        fullPath: '/admin/client-management',
        wrapClass: 'mypage_wrap',
        title: '클라이언트 관리',
        iconOff: '/publish/img/webp/menu_icon7_off.webp', // 변경
        iconOn: '/publish/img/webp/menu_icon7_on.webp', // 변경
        startsWithPaths: ['/admin/client-management'],
        section: 'admin',
      },
      USER_MANAGEMENT: {
        relativePath: 'user-management',
        fullPath: '/admin/user-management',
        wrapClass: 'mypage_wrap',
        title: '사용자 관리',
        iconOff: '/publish/img/webp/menu_icon7_off.webp', // 변경
        iconOn: '/publish/img/webp/menu_icon7_on.webp', // 변경
        startsWithPaths: ['/admin/user-management'],
        section: 'admin',
      },
      OFFLINE_LICENSE_MANAGEMENT: {
        relativePath: 'offline-license-management',
        fullPath: '/admin/offline-license-management',
        wrapClass: 'mypage_wrap',
        title: '오프라인 라이선스 관리',
        iconOff: '/publish/img/webp/menu_icon7_off.webp', // 변경
        iconOn: '/publish/img/webp/menu_icon7_on.webp', // 변경
        startsWithPaths: ['/admin/offline-license-management'],
        section: 'admin',
      },
      TERM_MANAGEMENT: {
        relativePath: 'term-management',
        fullPath: '/admin/term-management',
        wrapClass: 'mypage_wrap',
        title: '회원가입 약관 관리',
        iconOff: '/publish/img/webp/menu_icon7_off.webp', // 변경
        iconOn: '/publish/img/webp/menu_icon7_on.webp', // 변경
        startsWithPaths: ['/admin/term-management'],
        section: 'admin',
      },
      SELLER_TERM_MANAGEMENT: {
        relativePath: 'seller-term-management',
        fullPath: '/admin/seller-term-management',
        wrapClass: 'mypage_wrap',
        title: '전문가 약관 관리',
        iconOff: '/publish/img/webp/menu_icon7_off.webp', // 변경
        iconOn: '/publish/img/webp/menu_icon7_on.webp', // 변경
        startsWithPaths: ['/admin/seller-term-management'],
        section: 'admin',
      },
      FAQ_MANAGEMENT: {
        relativePath: 'faq-management',
        fullPath: '/admin/faq-management',
        wrapClass: 'mypage_wrap',
        title: 'FAQ 관리',
        iconOff: '/publish/img/webp/menu_icon4_off.webp', // 변경
        iconOn: '/publish/img/webp/menu_icon4_on.webp', // 변경
        section: 'admin',
      },
      ANALYTICS_DASHBOARD: {
        relativePath: 'analytics-dashboard',
        fullPath: '/admin/analytics-dashboard',
        wrapClass: 'mypage_wrap',
        title: '사용자 분석',
        iconOff: '/publish/img/webp/menu_icon4_off.webp', // 변경
        iconOn: '/publish/img/webp/menu_icon4_on.webp', // 변경
        section: 'admin',
      },
      EXTERNAL_POINT_MANAGEMENT: {
        relativePath: 'external-point-management',
        fullPath: '/admin/external-point-management',
        wrapClass: 'mypage_wrap',
        title: '외부 포인트 관리',
        iconOff: '/publish/img/webp/menu_icon7_off.webp',
        iconOn: '/publish/img/webp/menu_icon7_on.webp',
        startsWithPaths: ['/admin/external-point-management'],
        section: 'admin',
      },
    },
  },

  MY_PAGE: {
    basePath: '/my-page',
    routes: {
      CART: {
        relativePath: 'cart',
        fullPath: '/my-page/cart',
        wrapClass: 'mypage_wrap mypageCart_wrap',
        title: '장바구니',
        description: '선택한 상품의 목록과 결제 금액을 확인할 수 있습니다.',
        noteBoxKey: NOTE_BOX_KEYS.MY_CART_GUIDE,
        iconOff: '/publish/img/webp/menu_icon1_off.webp', // 변경
        iconOn: '/publish/img/webp/menu_icon1_on.webp', // 변경
        section: 'mypage',
      },
      PURCHASE: {
        relativePath: 'purchase',
        fullPath: '/my-page/purchase',
        wrapClass: 'mypage_wrap',
        title: '결제',
        description: '선택한 상품의 목록과 결제 금액을 확인할 수 있습니다.',
        iconOff: '',
        iconOn: '',
        section: 'mypage',
      },
      DEVICE: {
        relativePath: 'device',
        fullPath: '/my-page/device',
        wrapClass: 'mypage_wrap mypageDevice_wrap',
        title: '기기 관리',
        description: '등록된 기기들을 관리하고 모니터링할 수 있습니다.',
        noteBoxKey: NOTE_BOX_KEYS.MY_DEVICE_GUIDE,
        iconOff: '/publish/img/webp/menu_icon3_off.webp', // 변경
        iconOn: '/publish/img/webp/menu_icon3_on.webp', // 변경
        section: 'mypage',
      },
      ORDER_HISTORY: {
        relativePath: 'order-history',
        fullPath: '/my-page/order-history',
        wrapClass: 'mypage_wrap mypageOrder_wrap',
        title: '주문내역',
        description: '지금까지 주문한 상품과 이용 현황을 한곳에서 확인하세요.',
        noteBoxKey: NOTE_BOX_KEYS.MY_ORDER_HISTORY_GUIDE,
        iconOff: '/publish/img/webp/menu_icon2_off.webp', // 변경
        iconOn: '/publish/img/webp/menu_icon2_on.webp', // 변경
        startsWithPaths: ['/my-page/order-history'],
        section: 'mypage',
      },
      REFUND_HISTORY: {
        relativePath: 'refund-history',
        fullPath: '/my-page/refund-history',
        wrapClass: 'mypage_wrap mypageRefund_wrap',
        title: '환불내역',
        description: '환불 신청 내역과 처리 현황을 한눈에 확인할 수 있습니다.',
        noteBoxKey: NOTE_BOX_KEYS.MY_REFUND_HISTORY_GUIDE,
        iconOff: '/publish/img/webp/menu_icon4_off.webp', // 변경
        iconOn: '/publish/img/webp/menu_icon4_on.webp', // 변경
        startsWithPaths: ['/my-page/refund-history'],
        section: 'mypage',
      },
      TOKEN_HISTORY: {
        relativePath: 'token-history',
        fullPath: '/my-page/token-history',
        wrapClass: 'mypage_wrap mypageRecharge_wrap',
        title: '토큰 결제',
        description: '토큰 충전 및 사용 내역을 확인할 수 있습니다.',
        noteBoxKey: NOTE_BOX_KEYS.MY_TOKEN_HISTORY_GUIDE,
        iconOff: '/publish/img/webp/menu_icon5_off.webp', // 변경
        iconOn: '/publish/img/webp/menu_icon5_on.webp', // 변경
        section: 'mypage',
      },
      LICENSE: {
        relativePath: 'license',
        fullPath: '/my-page/license',
        wrapClass: 'mypage_wrap mypagePayment_wrap',
        title: '멤버십 결제',
        description: '필요한 멤버십을 선택하고 결제할 수 있습니다.',
        noteBoxKey: NOTE_BOX_KEYS.MY_LICENSE_GUIDE,
        iconOff: '/publish/img/webp/menu_icon6_off.webp', // 변경
        iconOn: '/publish/img/webp/menu_icon6_on.webp', // 변경
        section: 'mypage',
      },
      PROFILE_CHECK: {
        relativePath: 'profile-check',
        fullPath: '/my-page/profile-check',
        wrapClass: 'mypage_wrap',
        title: '회원정보',
        iconOff: '/publish/img/webp/menu_icon7_off.webp', // 변경
        iconOn: '/publish/img/webp/menu_icon7_on.webp', // 변경
        activePaths: ['/my-page/profile-check', '/my-page/profile'],
        section: 'mypage',
      },
      PROFILE: {
        relativePath: 'profile',
        fullPath: '/my-page/profile',
        wrapClass: 'mypage_wrap mypageMember_wrap',
        title: '회원정보',
        description: '회원 정보를 확인하고 수정할 수 있습니다.',
        noteBoxKey: NOTE_BOX_KEYS.MY_PROFILE_PRIVACY_GUIDE,
        iconOff: '/publish/img/webp/menu_icon7_off.webp', // 변경
        iconOn: '/publish/img/webp/menu_icon7_on.webp', // 변경
        section: 'mypage',
      },
      OFFLINE_LICENSE: {
        relativePath: 'offline-license',
        fullPath: '/my-page/offline-license',
        wrapClass: 'mypage_wrap',
        title: '오프라인 라이선스',
        description: '오프라인 환경에서 사용 가능한 라이선스 키를 확인할 수 있습니다.',
        iconOff: '/publish/img/webp/menu_icon7_off.webp',
        iconOn: '/publish/img/webp/menu_icon7_on.webp',
        section: 'mypage',
      },
      BUSINESS_CONVERSION: {
        relativePath: 'business-conversion',
        fullPath: '/my-page/business-conversion',
        wrapClass: 'mypage_wrap mypageMember_wrap',
        title: '기업 회원 전환',
        description: '기업용 서비스 이용을 위해 기업 정보를 입력해 주세요.',
        iconOff: '/publish/img/webp/menu_icon7_off.webp',
        iconOn: '/publish/img/webp/menu_icon7_on.webp',
        section: 'mypage',
      },
      SELLER_REGISTRATION: {
        relativePath: 'seller-registration',
        fullPath: '/my-page/seller-registration',
        wrapClass: 'mypage_wrap mypageSeller_wrap',
        title: '전문가 등록',
        noteBoxKey: NOTE_BOX_KEYS.MY_SELLER_REGISTRATION_GUIDE,
        iconOff: '/publish/img/webp/menu_icon8_off.webp', // 변경
        iconOn: '/publish/img/webp/menu_icon8_on.webp', // 변경
        section: 'seller',
        requiresSeller: false,
      },
      MY_INQUIRIES: {
        relativePath: 'my-inquiries',
        fullPath: '/my-page/my-inquiries',
        wrapClass: 'mypage_wrap mypageSellerRefund_wrap',
        title: '문의하기 내역',
        noteBoxKey: NOTE_BOX_KEYS.MY_INQUIRIES_GUIDE,
        iconOff: '/publish/img/webp/menu_icon9_off.webp', // 변경
        iconOn: '/publish/img/webp/menu_icon9_on.webp', // 변경
        section: 'mypage',
      },
      MY_ANALYSIS_REQUESTS: {
        relativePath: 'my-analysis-requests',
        fullPath: '/my-page/my-analysis-requests',
        wrapClass: 'mypage_wrap mypageSellerRefund_wrap',
        title: '분석의뢰 내역',
        noteBoxKey: NOTE_BOX_KEYS.MY_ANALYSIS_REQUESTS_GUIDE,
        iconOff: '/publish/img/webp/menu_icon10_off.webp', // 변경
        iconOn: '/publish/img/webp/menu_icon10_on.webp', // 변경
        section: 'mypage',
      },
    },
  },

  SELLER_MY_PAGE: {
    basePath: '/seller/my-page',
    routes: {
      PROFILE_EDIT: {
        relativePath: 'profile-edit',
        fullPath: '/seller/my-page/profile-edit',
        wrapClass: 'mypage_wrap mypageSeller_wrap',
        title: '전문가 프로필 편집',
        noteBoxKey: NOTE_BOX_KEYS.SELLER_PROFILE_EDIT_GUIDE,
        iconOff: '/publish/img/webp/menu_icon8_off.webp', // 변경
        iconOn: '/publish/img/webp/menu_icon8_on.webp', // 변경
        activePaths: ['/seller/my-page/profile-edit'],
        section: 'seller',
        requiresSeller: true,
      },
      PRODUCT_MANAGEMENT: {
        relativePath: 'product-management',
        fullPath: '/seller/my-page/product-management',
        wrapClass: 'mypage_wrap mypageProduct_wrap',
        title: '전문가 상품관리',
        noteBoxKey: NOTE_BOX_KEYS.SELLER_PRODUCT_MANAGEMENT_GUIDE,
        iconOff: '/publish/img/webp/menu_icon9_off.webp', // 변경
        iconOn: '/publish/img/webp/menu_icon9_on.webp', // 변경
        startsWithPaths: [
          '/seller/my-page/product-management',
          '/seller/my-page/product-add',
          '/seller/my-page/product-edit',
          '/seller/my-page/qna-management',
          '/seller/my-page/review-management',
        ],
        section: 'seller',
        requiresSeller: true,
      },
      PRODUCT_ADD: {
        relativePath: 'product-add',
        fullPath: '/seller/my-page/product-add',
        wrapClass: 'mypage_wrap mypageProduct_wrap',
        title: '상품 등록',
        noteBoxKey: NOTE_BOX_KEYS.SELLER_PRODUCT_MANAGEMENT_GUIDE,
        iconOff: '',
        iconOn: '',
        section: 'seller',
        requiresSeller: true,
      },
      PRODUCT_EDIT: {
        relativePath: 'product-edit',
        fullPath: '/seller/my-page/product-edit',
        wrapClass: 'mypage_wrap mypageProduct_wrap',
        title: '상품 수정',
        noteBoxKey: NOTE_BOX_KEYS.SELLER_PRODUCT_MANAGEMENT_GUIDE,
        iconOff: '',
        iconOn: '',
        section: 'seller',
        requiresSeller: true,
      },
      REFUND_MANAGEMENT: {
        relativePath: 'refund-management',
        fullPath: '/seller/my-page/refund-management',
        wrapClass: 'mypage_wrap mypageSellerRefund_wrap',
        title: '전문가 환불관리',
        noteBoxKey: NOTE_BOX_KEYS.SELLER_REFUND_MANAGEMENT_GUIDE,
        iconOff: '/publish/img/webp/menu_icon10_off.webp', // 변경
        iconOn: '/publish/img/webp/menu_icon10_on.webp', // 변경
        startsWithPaths: ['/seller/my-page/refund-management'],
        section: 'seller',
        requiresSeller: true,
      },
    },
  },
} as const;

/**
 * 마이페이지 메뉴 아이템 배열 (표시 순서대로)
 */
export const MY_PAGE_MENU_ITEMS: MenuItemConfig[] = [
  ROUTE_CONFIGS.MY_PAGE.routes.LICENSE,
  ROUTE_CONFIGS.MY_PAGE.routes.TOKEN_HISTORY,
  ROUTE_CONFIGS.MY_PAGE.routes.DEVICE,
  ROUTE_CONFIGS.MY_PAGE.routes.PROFILE,
  ROUTE_CONFIGS.MY_PAGE.routes.OFFLINE_LICENSE,
  ROUTE_CONFIGS.MY_PAGE.routes.BUSINESS_CONVERSION,
  ROUTE_CONFIGS.MY_PAGE.routes.CART,
  ROUTE_CONFIGS.MY_PAGE.routes.ORDER_HISTORY,
  ROUTE_CONFIGS.MY_PAGE.routes.REFUND_HISTORY,
  ROUTE_CONFIGS.MY_PAGE.routes.MY_ANALYSIS_REQUESTS,
  ROUTE_CONFIGS.MY_PAGE.routes.MY_INQUIRIES,
] as MenuItemConfig[];

/**
 * 전문가 메뉴 아이템 배열 (표시 순서대로)
 */
export const SELLER_MENU_ITEMS: MenuItemConfig[] = [
  ROUTE_CONFIGS.MY_PAGE.routes.SELLER_REGISTRATION, // 전문가가 아닐 때만 표시
  ROUTE_CONFIGS.SELLER_MY_PAGE.routes.PROFILE_EDIT, // 전문가일 때만 표시
  ROUTE_CONFIGS.SELLER_MY_PAGE.routes.PRODUCT_MANAGEMENT, // 전문가일 때만 표시
  ROUTE_CONFIGS.SELLER_MY_PAGE.routes.REFUND_MANAGEMENT, // 전문가일 때만 표시
] as MenuItemConfig[];

/**
 * 어드민 메뉴 아이템 배열 (표시 순서대로)
 */
export const ADMIN_MENU_ITEMS: MenuItemConfig[] = [
  ROUTE_CONFIGS.ADMIN.routes.ANALYTICS_DASHBOARD,
  ROUTE_CONFIGS.ADMIN.routes.USER_MANAGEMENT,
  ROUTE_CONFIGS.ADMIN.routes.OFFLINE_LICENSE_MANAGEMENT,
  ROUTE_CONFIGS.ADMIN.routes.SELLER_APPROVAL,
  ROUTE_CONFIGS.ADMIN.routes.PRODUCT_APPROVAL,
  ROUTE_CONFIGS.ADMIN.routes.ANALYSIS_REQUEST,
  ROUTE_CONFIGS.ADMIN.routes.INQUIRY,
  ROUTE_CONFIGS.ADMIN.routes.FAQ_MANAGEMENT,
  ROUTE_CONFIGS.ADMIN.routes.EDUCATION_MANAGEMENT,
  ROUTE_CONFIGS.ADMIN.routes.CLIENT_MANAGEMENT,
  ROUTE_CONFIGS.ADMIN.routes.EXTERNAL_POINT_MANAGEMENT,
  ROUTE_CONFIGS.ADMIN.routes.TERM_MANAGEMENT,
  ROUTE_CONFIGS.ADMIN.routes.SELLER_TERM_MANAGEMENT,
] as MenuItemConfig[];

/**
 * 경로에서 wrap 클래스 가져오기 (정확한 매칭 및 startsWith 매칭 사용)
 */
export const getWrapClass = (pathname: string): string => {
  // 동적 경로 매칭 (상세 페이지 등) - 먼저 체크
  for (const config of Object.values(ROUTE_CONFIGS)) {
    if ('path' in config && config.path.includes(':')) {
      // 동적 경로 패턴 매칭 (예: /products/:id)
      const pathPattern = config.path.replace(/:[^/]+/g, '[^/]+');
      const regex = new RegExp(`^${pathPattern}$`);
      if (regex.test(pathname)) {
        return `wrap ${config.wrapClass}`;
      }
    }
  }

  // 단일 경로 매칭
  for (const config of Object.values(ROUTE_CONFIGS)) {
    if ('path' in config && config.path === pathname) {
      return `wrap ${config.wrapClass}`;
    }

    // 계층 구조 매칭
    if ('routes' in config) {
      for (const route of Object.values(config.routes)) {
        // 정확한 경로 매칭
        if (route.fullPath === pathname) {
          return `wrap ${route.wrapClass}`;
        }
        // startsWithPaths로 시작하는 경로 매칭 (상세 페이지 등)
        if (route.startsWithPaths) {
          for (const startsWithPath of route.startsWithPaths) {
            if (pathname.startsWith(startsWithPath)) {
              return `wrap ${route.wrapClass}`;
            }
          }
        }
      }
    }
  }

  // 기본값
  return 'wrap';
};

/**
 * 경로에서 페이지 제목 가져오기 (정확한 매칭 및 startsWith 매칭 사용)
 */
export const getPageTitle = (pathname: string): string | null => {
  // 단일 경로 매칭 (공통 라우트)
  for (const config of Object.values(ROUTE_CONFIGS)) {
    if ('path' in config && config.path === pathname && config.title) {
      return config.title;
    }

    // 계층 구조 매칭 (routes가 있는 경우)
    if ('routes' in config) {
      for (const route of Object.values(config.routes)) {
        // 정확한 경로 매칭
        if (route.fullPath === pathname && route.title) {
          return route.title;
        }
        // startsWithPaths로 시작하는 경로 매칭 (상세 페이지 등)
        if (route.startsWithPaths) {
          for (const startsWithPath of route.startsWithPaths) {
            if (pathname.startsWith(startsWithPath) && route.title) {
              return route.title;
            }
          }
        }
        // activePaths로 정확한 경로 매칭
        if (route.activePaths) {
          for (const activePath of route.activePaths) {
            if (activePath === pathname && route.title) {
              return route.title;
            }
          }
        }
      }
    }
  }

  // 기본값 (title이 없으면 null 반환)
  return null;
};
